// =====================================================================
// Where the seconds go between accepting a bridged call and hearing the lead.
//
// Production: time to audible is ~300ms at 33ms RTT, ~2,900ms at 170ms, ~5,900ms at
// 360ms. It scales with round-trip time and each SDR sits tightly in one mode, so it is
// deterministic rather than lossy — but the implied number of round trips is roughly
// three times what ICE (~1-3) plus DTLS (~2-3) should cost. **The mechanism is unknown.**
// This module exists to find it, not to confirm it.
//
// Three previous fields each timed something adjacent to the question:
//
//   first_sample_ms  fired on the SDK's 1Hz timer, so it read ~1005ms for every SDR on
//                    every continent. It timed the clock, not the network. Removed.
//   first_media_ms   is real RTP, but quantised to +/-1s by that same 1Hz timer. On a
//                    ~3s phenomenon the error is the size of the answer.
//   first_volume_ms  fires from the peer connection's `ontrack`
//                    (lib/twilio/rtc/peerconnection.ts:617), during setRemoteDescription
//                    — before ICE, before DTLS, before any packet. It reads ~25ms.
//
// So this reads the RTCPeerConnection itself and marks the phases between accept and
// audio: candidates, connectivity checks, DTLS, first packet, first packet with any
// audio energy. The marks are exhaustive across the window, so whatever spends the time
// has to land between two adjacent ones.
//
// READ THE ENERGY MARK WITH CARE. `totalAudioEnergy` accumulates over played-out samples,
// and RFC 3389 comfort noise (plus ordinary PSTN line noise) decodes to non-zero
// amplitude — so a >0 gate plausibly latches on the first frame of any real leg, making
// `first_inbound_energy_ms` a restatement of `first_inbound_rtp_ms`. That is a FOURTH
// adjacent field, and it is why the raw `inbound_bytes` and `inbound_audio_energy` are
// emitted alongside: if `energy_ms - rtp_ms <= poll_interval_ms` on most rows, the mark
// collapsed and only a thresholded measure would separate "Twilio sends nothing" from
// "Twilio sends an empty leg". The question is left falsifiable rather than assumed.
//
// Pure folds + one guarded private reach, mirroring audio-liveness.ts: the state bag is
// clock-free and the caller owns performance.now(), so the parse is testable without a
// browser, a peer connection, or a call.
// =====================================================================

// How often the peer connection is polled. Nothing in WebRTC signals the first RTP
// packet, or the first one carrying audio, so those marks can only come from stats. 250ms
// is 4x finer than the SDK's own 1Hz monitor (the quantisation this exists to escape).
const POLL_MS = 250;
// Long enough to cover the worst observed wait without leaving a timer alive on a call
// that never gets media.
const MAX_POLL_MS = 20_000;

// Only these. An ICE server URL is split for its scheme, and anything that is not a known
// scheme is dropped rather than emitted — a malformed entry must not put a TURN hostname
// into a log facet.
const ICE_SCHEMES = new Set(['stun', 'stuns', 'turn', 'turns']);

export interface StatLike {
  type?: string;
  id?: string;
  [key: string]: unknown;
}

export interface StatsReportLike {
  forEach(cb: (stat: StatLike) => void): void;
}

export interface PcEventLike {
  candidate?: { type?: string; candidate?: string } | null;
  // Some thin-client stacks report the state only on the event target — the SDK carries
  // the same fallback (lib/twilio/rtc/peerconnection.ts:657, "VDI environment").
  target?: { connectionState?: string; connectionState_?: string };
}

// Structural, and never imported from the SDK. Note what that does NOT buy: the reach
// below takes `unknown`, so an SDK shape change cannot surface as a compile error — it
// surfaces in the data as `setup_probe: 'no_pc'`. That is the tripwire, and it is the
// only warning there is.
export interface PeerConnectionLike {
  addEventListener(type: string, listener: (ev: PcEventLike) => void): void;
  removeEventListener(type: string, listener: (ev: PcEventLike) => void): void;
  getStats(): Promise<StatsReportLike>;
  getConfiguration?(): {
    iceServers?: { urls?: string | string[] }[];
    iceTransportPolicy?: string;
  };
  iceGatheringState?: string;
  iceConnectionState?: string;
  connectionState?: string;
  signalingState?: string;
  // Read only for `a=setup:`. Optional for the same reason as everything else here: a
  // shape change must surface in the data, never as a compile error.
  currentLocalDescription?: { sdp?: string } | null;
  currentRemoteDescription?: { sdp?: string } | null;
}

export interface StatsSnapshot {
  inboundBytes: number | null;
  inboundPackets: number | null;
  outboundPackets: number | null;
  // Cumulative, and non-zero for comfort noise as well as speech — see the header.
  inboundAudioEnergy: number | null;
  localType: string | null;
  remoteType: string | null;
  protocol: string | null;
  // Non-standard and Chrome-only, so relay analysis is silently Chrome-biased. `protocol`
  // reads udp on nearly every row and cannot tell a cheap UDP relay from TURN-over-TLS.
  relayProtocol: string | null;
  requestsSent: number | null;
  responsesReceived: number | null;
  pairRttMs: number | null;
  dtlsState: string | null;

  // Transport-level counters. These EXCLUDE ICE connectivity checks: the spec defines
  // bytesSent as payload "not including headers, padding or ICE connectivity checks", and
  // Chrome's STUN pings bypass the connection counters these are read from. So the
  // difference between two latched readings is already DTLS traffic alone, and the STUN
  // counters below must NOT be subtracted from it — doing so would under-count packets
  // and overstate mean flight size, which is the one error an MTU comparison cannot
  // survive.
  //
  // One asymmetry the spec leaves open: it excludes ICE checks from bytesSent explicitly
  // but says only "total number of packets sent" for packetsSent. Chrome sources both
  // from the same counter, so the ratio is consistent in practice — but that is
  // implementation, not guarantee, and a non-Chrome row should not be trusted for it.
  transportPacketsSent: number | null;
  transportPacketsReceived: number | null;
  transportBytesSent: number | null;
  transportBytesReceived: number | null;

  // Populated only once the handshake completes, so these read null at the ice-time latch
  // by design. The cipher suite name carries ECDSA or RSA literally, which settles the
  // certificate-size question from production rather than by reading SDK source.
  dtlsCipher: string | null;
  dtlsRole: string | null;
  tlsVersion: string | null;

  // How many times the selected pair moved. Non-zero means the path changed mid-setup,
  // which restarts the handshake — an explanation neither standing hypothesis covers.
  selectedCandidatePairChanges: number | null;

  // Decoded DER length summed over the chain, NEVER the certificate itself. Local is the
  // browser's own; remote is the far end's, and is the one that could be a chain large
  // enough to fragment on a 1,492-byte path.
  localCertBytes: number | null;
  remoteCertBytes: number | null;

  // Send-side failures: a packet the browser never got onto the wire at all. A different
  // failure from one lost in transit, and invisible in every field logged today.
  packetsDiscardedOnSend: number | null;
  bytesDiscardedOnSend: number | null;

  // A SUBSET of requestsSent, not a separate population: Chrome computes it as
  // sent_ping_requests_total minus sent_ping_requests_before_first_response. Summing the
  // two double-counts. The quantity worth querying is requestsSent - consentRequestsSent,
  // the checks sent before anything answered.
  consentRequestsSent: number | null;

  // Cumulative seconds, divided by responsesReceived for a mean. `pairRttMs` is the
  // latest single sample, and a multi-second phenomenon deserves better than one reading
  // taken at whatever moment the poll happened to land.
  totalRoundTripTime: number | null;

  // Chromium-only and being withdrawn for fingerprinting reasons, so expect nulls. Worth
  // reading because it separates wired from wifi from cellular without asking anyone.
  localNetworkType: string | null;
}

export interface SetupTiming {
  startedAt: number;
  // When accept() was invoked, if the caller knows. The gap to `startedAt` is local SDP
  // work and should be tens of ms.
  acceptInvokedAt: number | null;
  // The ATTACH outcome, and nothing else. A fold that throws later increments
  // `probeErrors` instead, so a transient throw cannot disqualify an otherwise good row
  // from a `setup_probe:ok` filter — that would bias the dataset toward whatever
  // correlates with the throw.
  probe: 'ok' | 'not_attached' | 'no_pc';
  probeErrors: number;
  stopped: boolean;
  // State at attach. The probe can only attach once accept() has built the peer
  // connection, by which point transitions have already fired — these make "already done
  // before we looked" distinguishable from "never happened".
  attachIceState: string | null;
  attachGatheringState: string | null;
  // Signalling has NO elapsed mark, deliberately. `accept` is emitted synchronously from
  // inside the pc's signalingstatechange handler (peerconnection.ts:644), and the DOM
  // copies the listener list before dispatch — so a listener added during accept never
  // sees that event. An elapsed field could only ever be null, or mean "this call
  // renegotiated", which is a different fact wearing a setup-cost name.
  attachSignalingState: string | null;
  firstCandidateMs: number | null;
  firstRelayCandidateMs: number | null;
  gatheringCompleteMs: number | null;
  candidates: number;
  candidateTypes: Set<string>;
  iceCheckingMs: number | null;
  iceConnectedMs: number | null;
  // Last ICE state seen, so `failed` / `disconnected` is distinguishable from "never
  // progressed" — both leave iceConnectedMs null.
  iceLastState: string | null;
  pcConnectedMs: number | null;
  dtlsConnectedMs: number | null;
  firstOutboundRtpMs: number | null;
  firstInboundRtpMs: number | null;
  firstInboundEnergyMs: number | null;
  iceServers: number | null;
  iceServerSchemes: string | null;
  iceTransportPolicy: string | null;
  // Rolling, overwritten every poll. Latching at first-inbound-RTP would leave the calls
  // where media never arrived — the ones the relay and dropped-check diagnostics exist
  // for — with no candidate pair at all.
  snapshot: StatsSnapshot | null;

  // `snapshot` is rolling, so by the time the row is emitted it holds settle-time values
  // only. Sizing the handshake needs a BEFORE as well, so the ice-time reading is latched
  // separately. Both are quantised to POLL_MS — the deltas are bounded by that
  // resolution rather than exact instants, and `setup_poll_interval_ms` is already on the
  // row, so that limit is self-describing.
  atIceConnected: StatsSnapshot | null;
  atDtlsConnected: StatsSnapshot | null;

  // `a=setup:` from each side. Only the four legal tokens are ever stored.
  sdpLocalRole: string | null;
  sdpRemoteRole: string | null;

  // navigator.connection at attach: the independent read on whether an SDR is on wifi,
  // wired or tethered.
  connType: string | null;
  connEffectiveType: string | null;
  connDownlinkMbps: number | null;
  connRttMs: number | null;
}

export function newSetupTiming(startedAt: number, acceptInvokedAt?: number | null): SetupTiming {
  return {
    startedAt,
    acceptInvokedAt: acceptInvokedAt ?? null,
    probe: 'not_attached',
    probeErrors: 0,
    stopped: false,
    attachIceState: null,
    attachGatheringState: null,
    attachSignalingState: null,
    firstCandidateMs: null,
    firstRelayCandidateMs: null,
    gatheringCompleteMs: null,
    candidates: 0,
    candidateTypes: new Set<string>(),
    iceCheckingMs: null,
    iceConnectedMs: null,
    iceLastState: null,
    pcConnectedMs: null,
    dtlsConnectedMs: null,
    firstOutboundRtpMs: null,
    firstInboundRtpMs: null,
    firstInboundEnergyMs: null,
    iceServers: null,
    iceServerSchemes: null,
    iceTransportPolicy: null,
    snapshot: null,
    atIceConnected: null,
    atDtlsConnected: null,
    sdpLocalRole: null,
    sdpRemoteRole: null,
    connType: null,
    connEffectiveType: null,
    connDownlinkMbps: null,
    connRttMs: null,
  };
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

const since = (t: SetupTiming, now: number): number => Math.max(0, Math.round(now - t.startedAt));

export function observeState(
  t: SetupTiming,
  kind: 'ice' | 'pc' | 'gathering',
  state: string,
  now: number,
): void {
  if (t.stopped) return;
  const at = since(t, now);
  if (kind === 'ice') {
    if (state) t.iceLastState = state;
    if (state === 'checking' && t.iceCheckingMs == null) t.iceCheckingMs = at;
    // `completed` counts: it is `connected` plus finished checks, and a call can go
    // straight there. Waiting only for `connected` would leave the mark null on the
    // fastest paths — biased in exactly the wrong direction.
    if ((state === 'connected' || state === 'completed') && t.iceConnectedMs == null) {
      t.iceConnectedMs = at;
    }
    return;
  }
  if (kind === 'pc') {
    if (state === 'connected' && t.pcConnectedMs == null) t.pcConnectedMs = at;
    return;
  }
  if (state === 'complete' && t.gatheringCompleteMs == null) t.gatheringCompleteMs = at;
}

export function observeCandidate(t: SetupTiming, type: string | null, now: number): void {
  if (t.stopped) return;
  const at = since(t, now);
  t.candidates += 1;
  if (t.firstCandidateMs == null) t.firstCandidateMs = at;
  if (type) {
    t.candidateTypes.add(type);
    if (type === 'relay' && t.firstRelayCandidateMs == null) t.firstRelayCandidateMs = at;
  }
}

export function observeStats(t: SetupTiming, s: StatsSnapshot, now: number): void {
  if (t.stopped) return;
  const at = since(t, now);
  if (t.firstOutboundRtpMs == null && (s.outboundPackets ?? 0) > 0) t.firstOutboundRtpMs = at;
  if (t.firstInboundRtpMs == null && (s.inboundBytes ?? 0) > 0) t.firstInboundRtpMs = at;
  if (t.firstInboundEnergyMs == null && (s.inboundAudioEnergy ?? 0) > 0) {
    t.firstInboundEnergyMs = at;
  }
  if (t.dtlsConnectedMs == null && s.dtlsState === 'connected') t.dtlsConnectedMs = at;

  // ICE is known-connected either because the event fired, or because it had already
  // happened before the probe could attach — `attachIceState` exists precisely to keep
  // those two distinguishable.
  //
  // NOTE the absence of a `dtlsState !== 'connected'` guard on the first latch. Excluding
  // the poll where DTLS is already done would suppress the ice-time latch permanently on
  // any call whose handshake finished inside a single poll — which is the normal shape
  // for a low-latency path, i.e. exactly the fast baseline the slow population has to be
  // measured against. Both latches taking the same reading yields zero deltas, which is a
  // fact; a null latch is an absence, and a query cannot tell those apart.
  //
  // The `dtlsState` arm closes the other route to that same loss. This fold runs from a
  // getStats continuation, which is NOT ordered against the iceconnectionstatechange
  // task, so a poll can see DTLS already connected while `iceConnectedMs` is still null.
  // Without this arm the ice latch would then either take a reading from AFTER the
  // handshake (inverting the two, which reads downstream as an unmeasurable window rather
  // than as the error it is) or never fire at all, if the same poll settles the clocks and
  // stops the timer. DTLS cannot complete before ICE is up, so observing it is proof
  // enough on its own and makes the pair order-independent.
  const iceUp =
    t.iceConnectedMs != null ||
    t.attachIceState === 'connected' ||
    t.attachIceState === 'completed' ||
    s.dtlsState === 'connected';
  if (t.atIceConnected == null && iceUp) t.atIceConnected = s;
  if (t.atDtlsConnected == null && s.dtlsState === 'connected') t.atDtlsConnected = s;

  t.snapshot = s;
}

// Only these four. `a=setup:` is an enum in RFC 4145; anything else is a malformed line,
// and emitting it raw would put peer-controlled text into a log facet.
const SETUP_ROLES = new Set(['active', 'passive', 'actpass', 'holdconn']);

// Which side sends the DTLS ClientHello. A browser that is `passive` is the DTLS SERVER —
// waiting for the far end to start — which would reframe a slow handshake from "ours is
// slow" to "nobody told the other end to begin".
//
// Parses that attribute and NOTHING else: SDP also carries IP addresses, ice-ufrag and
// DTLS fingerprints, none of which may reach a log. Takes the LAST match, because a
// session-level `a=setup:` can precede and differ from the m-line's, and the media-level
// value is the operative one.
export function parseSetupRole(sdp: unknown): string | null {
  if (typeof sdp !== 'string') return null;
  const all = sdp.match(/^a=setup:(\w+)/gm);
  const role = all?.[all.length - 1]?.split(':')[1]?.toLowerCase();
  return role && SETUP_ROLES.has(role) ? role : null;
}

// Latch-once, so a renegotiation cannot overwrite the roles that produced the timings
// sitting on the same row.
export function observeSdp(t: SetupTiming, local: unknown, remote: unknown): void {
  if (t.stopped) return;
  if (t.sdpLocalRole == null) t.sdpLocalRole = parseSetupRole(local);
  if (t.sdpRemoteRole == null) t.sdpRemoteRole = parseSetupRole(remote);
}

// Every clock this poll can still move has been stamped, so there is nothing left to
// learn by polling. Lives here rather than in the probe so the stop condition and the
// folds cannot drift apart.
export function setupSettled(t: SetupTiming): boolean {
  return (
    t.firstOutboundRtpMs != null &&
    t.firstInboundRtpMs != null &&
    t.firstInboundEnergyMs != null &&
    t.dtlsConnectedMs != null
  );
}

const isAudio = (s: StatLike): boolean => {
  const kind = str(s['kind']) ?? str(s['mediaType']);
  // Absent kind means an older report with a single audio track — this connection only
  // ever carries audio, so treating it as audio is right and dropping it would be wrong.
  return kind == null || kind === 'audio';
};

export function parseStats(report: StatsReportLike): StatsSnapshot {
  const all: StatLike[] = [];
  try {
    report.forEach((s) => {
      if (s && typeof s === 'object') all.push(s);
    });
  } catch {
    // Fall through: an empty list produces the all-null shape by the same code path.
    all.length = 0;
  }

  const byId = new Map<string, StatLike>();
  for (const s of all) if (s.id) byId.set(s.id, s);

  // The active transport is the one naming a selected pair. Firefox omits that and flags
  // the pair instead. Deliberately NO "any succeeded pair" fallback: several pairs can be
  // `succeeded` at once while only one carries media, so guessing would report a host
  // pair while a relay was in use — understating exactly what this is meant to detect.
  const transport = all.find((s) => s.type === 'transport' && str(s['selectedCandidatePairId']));
  const pairId = transport ? str(transport['selectedCandidatePairId']) : null;
  const pair =
    (pairId ? byId.get(pairId) : undefined) ??
    all.find((s) => s.type === 'candidate-pair' && s['selected'] === true) ??
    null;

  const localId = pair ? str(pair['localCandidateId']) : null;
  const remoteId = pair ? str(pair['remoteCandidateId']) : null;
  const local = localId ? byId.get(localId) : undefined;
  const remote = remoteId ? byId.get(remoteId) : undefined;

  const inbound = all.find((s) => s.type === 'inbound-rtp' && isAudio(s));
  const outbound = all.find((s) => s.type === 'outbound-rtp' && isAudio(s));
  const anyTransport = transport ?? all.find((s) => s.type === 'transport');

  // currentRoundTripTime is SECONDS in the spec.
  const rttSeconds = pair ? num(pair['currentRoundTripTime']) : null;

  // DECODED length, never the value. The comparison this exists for is against a path
  // MTU in bytes, and base64 is ~4/3 of the DER it encodes — an inflated number compared
  // against an MTU is a wrong answer, not a noisy one.
  const derBytes = (b64: string): number => {
    const pad = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
    return Math.max(0, Math.floor((b64.length * 3) / 4) - pad);
  };
  // Walks issuerCertificateId, because `base64Certificate` is the leaf alone while the
  // handshake flight carries the whole chain. Depth-capped and visited-guarded so a
  // cyclic or self-referential chain cannot spin the poll.
  const chainBytes = (id: string | null): number | null => {
    let total: number | null = null;
    const seen = new Set<string>();
    let cur = id;
    for (let i = 0; i < 8 && cur && !seen.has(cur); i += 1) {
      seen.add(cur);
      const cert = byId.get(cur);
      if (!cert) break;
      const b64 = str(cert['base64Certificate']);
      if (b64 != null) total = (total ?? 0) + derBytes(b64);
      cur = str(cert['issuerCertificateId']);
    }
    return total;
  };

  return {
    inboundBytes: inbound ? num(inbound['bytesReceived']) : null,
    inboundPackets: inbound ? num(inbound['packetsReceived']) : null,
    outboundPackets: outbound ? num(outbound['packetsSent']) : null,
    // totalAudioEnergy is cumulative and the field we want. audioLevel is instantaneous
    // and only a fallback for browsers omitting the total — read between words it looks
    // like silence, so it is strictly worse, not equivalent.
    inboundAudioEnergy: inbound
      ? (num(inbound['totalAudioEnergy']) ?? num(inbound['audioLevel']))
      : null,
    localType: local ? str(local['candidateType']) : null,
    remoteType: remote ? str(remote['candidateType']) : null,
    protocol: local ? str(local['protocol']) : null,
    relayProtocol: local ? str(local['relayProtocol']) : null,
    requestsSent: pair ? num(pair['requestsSent']) : null,
    responsesReceived: pair ? num(pair['responsesReceived']) : null,
    pairRttMs: rttSeconds == null ? null : Math.round(rttSeconds * 1000),
    dtlsState: anyTransport ? str(anyTransport['dtlsState']) : null,
    transportPacketsSent: anyTransport ? num(anyTransport['packetsSent']) : null,
    transportPacketsReceived: anyTransport ? num(anyTransport['packetsReceived']) : null,
    transportBytesSent: anyTransport ? num(anyTransport['bytesSent']) : null,
    transportBytesReceived: anyTransport ? num(anyTransport['bytesReceived']) : null,
    dtlsCipher: anyTransport ? str(anyTransport['dtlsCipher']) : null,
    dtlsRole: anyTransport ? str(anyTransport['dtlsRole']) : null,
    tlsVersion: anyTransport ? str(anyTransport['tlsVersion']) : null,
    selectedCandidatePairChanges: anyTransport
      ? num(anyTransport['selectedCandidatePairChanges'])
      : null,
    localCertBytes: chainBytes(anyTransport ? str(anyTransport['localCertificateId']) : null),
    remoteCertBytes: chainBytes(anyTransport ? str(anyTransport['remoteCertificateId']) : null),
    packetsDiscardedOnSend: pair ? num(pair['packetsDiscardedOnSend']) : null,
    bytesDiscardedOnSend: pair ? num(pair['bytesDiscardedOnSend']) : null,
    consentRequestsSent: pair ? num(pair['consentRequestsSent']) : null,
    totalRoundTripTime: pair ? num(pair['totalRoundTripTime']) : null,
    localNetworkType: local ? str(local['networkType']) : null,
  };
}

// Fields declared as well as indexed, exactly as PreflightLog does: with only the index
// signature the report literal is unchecked, and a renamed key — which every dashboard
// binds to — would not be a type error.
export interface SetupLog {
  [key: string]: string | number | boolean | null;
  setup_probe: string;
  setup_probe_errors: number | null;
  setup_accept_ms: number | null;
  setup_attach_ice_state: string | null;
  setup_attach_gathering_state: string | null;
  setup_attach_signaling_state: string | null;
  setup_first_candidate_ms: number | null;
  setup_first_relay_candidate_ms: number | null;
  setup_gathering_complete_ms: number | null;
  setup_candidates: number | null;
  setup_candidate_types: string | null;
  setup_ice_checking_ms: number | null;
  setup_ice_connected_ms: number | null;
  setup_ice_last_state: string | null;
  setup_pc_connected_ms: number | null;
  setup_dtls_connected_ms: number | null;
  setup_first_outbound_rtp_ms: number | null;
  setup_first_inbound_rtp_ms: number | null;
  setup_first_inbound_energy_ms: number | null;
  setup_inbound_bytes: number | null;
  setup_inbound_packets: number | null;
  setup_inbound_audio_energy: number | null;
  setup_selected_local_type: string | null;
  setup_selected_remote_type: string | null;
  setup_selected_protocol: string | null;
  setup_relay_protocol: string | null;
  setup_ice_requests_sent: number | null;
  setup_ice_responses_received: number | null;
  setup_pair_rtt_ms: number | null;
  setup_dtls_state: string | null;
  setup_ice_servers: number | null;
  setup_ice_server_schemes: string | null;
  setup_ice_transport_policy: string | null;
  setup_poll_interval_ms: number;

  // Transport counters at both ends of the handshake window. Raw endpoints rather than
  // only the difference: if the ice-time latch never fires, a delta-only design would be
  // null and everything here would be lost, whereas the dtls-time totals alone still size
  // the whole setup. Subtraction is trivial in a query; the division below is not.
  setup_transport_at_ice_packets_sent: number | null;
  setup_transport_at_ice_packets_received: number | null;
  setup_transport_at_ice_bytes_sent: number | null;
  setup_transport_at_ice_bytes_received: number | null;
  setup_transport_at_dtls_packets_sent: number | null;
  setup_transport_at_dtls_packets_received: number | null;
  setup_transport_at_dtls_bytes_sent: number | null;
  setup_transport_at_dtls_bytes_received: number | null;

  // Connectivity-check volume at the same two points. These are a SEPARATE population
  // from the transport counters above, which already exclude ICE checks — do NOT subtract
  // one from the other. They answer how much checking and re-checking the path needed,
  // which is its own explanation for a slow setup.
  //
  // Note the infix: `_at_ice_` means "latched when ICE came up", whereas the older
  // `setup_ice_requests_sent` is the same underlying stat read at settle. Same stat,
  // different instant.
  setup_stun_at_ice_requests_sent: number | null;
  setup_stun_at_ice_responses_received: number | null;
  setup_stun_at_ice_consent_requests_sent: number | null;
  setup_stun_at_dtls_requests_sent: number | null;
  setup_stun_at_dtls_responses_received: number | null;
  setup_stun_at_dtls_consent_requests_sent: number | null;

  // Mean packet size across the two latches: large values mean flights big enough for a
  // reduced-MTU path to fragment, small ones rule fragmentation out. Because the counters
  // exclude ICE checks, this is DTLS traffic alone with nothing to net out.
  setup_handshake_avg_bytes_sent: number | null;
  setup_handshake_avg_bytes_received: number | null;

  // Named apart from the two averages above ON PURPOSE, because it is not the window they
  // divide across. This is the event clock (ICE connected -> DTLS connected); the averages
  // span two polls, quantised to POLL_MS. They usually agree, but where ICE completed
  // before the probe attached the latches fire while this stays null, so a query dividing
  // one by the other would silently drop that population.
  setup_ice_to_dtls_ms: number | null;

  setup_dtls_cipher: string | null;
  setup_dtls_role: string | null;
  setup_tls_version: string | null;
  setup_selected_pair_changes: number | null;
  setup_sdp_local_role: string | null;
  setup_sdp_remote_role: string | null;
  setup_local_cert_bytes: number | null;
  setup_remote_cert_bytes: number | null;
  setup_ice_packets_discarded_on_send: number | null;
  setup_ice_bytes_discarded_on_send: number | null;
  // Named apart from `setup_pair_rtt_ms` deliberately: that is the latest single sample,
  // this is the cumulative mean over every STUN response. One typo apart would have been
  // one typo from a dashboard silently reading the wrong series.
  setup_stun_rtt_mean_ms: number | null;
  setup_local_network_type: string | null;
  setup_conn_type: string | null;
  setup_conn_effective_type: string | null;
  setup_conn_downlink_mbps: number | null;
  setup_conn_rtt_ms: number | null;
}

// Null rather than 0 when the window is unmeasurable: a 0 would read as "no traffic
// during the handshake", which is a finding, not a missing measurement. Parameters accept
// undefined because the latches are optional-chained at the call sites.
function avgPacketBytes(
  bytesBefore: number | null | undefined,
  bytesAfter: number | null | undefined,
  packetsBefore: number | null | undefined,
  packetsAfter: number | null | undefined,
): number | null {
  if (bytesBefore == null || bytesAfter == null) return null;
  if (packetsBefore == null || packetsAfter == null) return null;
  const packets = packetsAfter - packetsBefore;
  const bytes = bytesAfter - bytesBefore;
  // A negative delta means the two readings did not come from one transport — an ICE
  // restart, or a non-bundled connection where the no-selected-pair fallback picked a
  // different transport on each poll. Cumulative counters cannot otherwise go backwards.
  // Emitting the negative "mean packet size" that would result puts nonsense next to real
  // values in the same aggregation.
  return packets > 0 && bytes >= 0 ? Math.round(bytes / packets) : null;
}

// Every key is always present. Nulls mean "not observed" — and the counters are null
// rather than 0 when the probe never attached, so "we saw zero candidates" and "we never
// looked" stay distinguishable in a query.
//
// The snapshot-derived counters are read at the poll where the clocks settled, NOT at
// call end (polling stops once settled), so they are early-call values, not call totals.
// The `_at_ice_` / `_at_dtls_` families are narrower still: each is latched at one poll,
// so both are quantised to POLL_MS and a difference between them is bounded by that
// resolution rather than being an exact instant.
export function setupReport(t: SetupTiming | null): SetupLog {
  const s = t?.snapshot ?? null;
  const ice = t?.atIceConnected ?? null;
  const dtls = t?.atDtlsConnected ?? null;
  return {
    setup_probe: t?.probe ?? 'not_attached',
    setup_probe_errors: t?.probeErrors ?? null,
    setup_accept_ms:
      t == null || t.acceptInvokedAt == null
        ? null
        : Math.max(0, Math.round(t.startedAt - t.acceptInvokedAt)),
    setup_attach_ice_state: t?.attachIceState ?? null,
    setup_attach_gathering_state: t?.attachGatheringState ?? null,
    setup_attach_signaling_state: t?.attachSignalingState ?? null,
    setup_first_candidate_ms: t?.firstCandidateMs ?? null,
    setup_first_relay_candidate_ms: t?.firstRelayCandidateMs ?? null,
    setup_gathering_complete_ms: t?.gatheringCompleteMs ?? null,
    setup_candidates: t?.candidates ?? null,
    setup_candidate_types:
      t && t.candidateTypes.size > 0 ? [...t.candidateTypes].sort().join(',') : null,
    setup_ice_checking_ms: t?.iceCheckingMs ?? null,
    setup_ice_connected_ms: t?.iceConnectedMs ?? null,
    setup_ice_last_state: t?.iceLastState ?? null,
    setup_pc_connected_ms: t?.pcConnectedMs ?? null,
    setup_dtls_connected_ms: t?.dtlsConnectedMs ?? null,
    setup_first_outbound_rtp_ms: t?.firstOutboundRtpMs ?? null,
    setup_first_inbound_rtp_ms: t?.firstInboundRtpMs ?? null,
    setup_first_inbound_energy_ms: t?.firstInboundEnergyMs ?? null,
    // The two raw readings behind the marks above, so the energy question is answerable
    // from the data rather than assumed — see the header.
    setup_inbound_bytes: s?.inboundBytes ?? null,
    setup_inbound_packets: s?.inboundPackets ?? null,
    setup_inbound_audio_energy: s?.inboundAudioEnergy ?? null,
    setup_selected_local_type: s?.localType ?? null,
    setup_selected_remote_type: s?.remoteType ?? null,
    setup_selected_protocol: s?.protocol ?? null,
    setup_relay_protocol: s?.relayProtocol ?? null,
    setup_ice_requests_sent: s?.requestsSent ?? null,
    setup_ice_responses_received: s?.responsesReceived ?? null,
    setup_pair_rtt_ms: s?.pairRttMs ?? null,
    setup_dtls_state: s?.dtlsState ?? null,
    setup_ice_servers: t?.iceServers ?? null,
    setup_ice_server_schemes: t?.iceServerSchemes ?? null,
    setup_ice_transport_policy: t?.iceTransportPolicy ?? null,
    // On the row so the resolution of every polled mark is self-describing, and a later
    // change to POLL_MS cannot silently reinterpret old data.
    setup_poll_interval_ms: POLL_MS,

    setup_transport_at_ice_packets_sent: ice?.transportPacketsSent ?? null,
    setup_transport_at_ice_packets_received: ice?.transportPacketsReceived ?? null,
    setup_transport_at_ice_bytes_sent: ice?.transportBytesSent ?? null,
    setup_transport_at_ice_bytes_received: ice?.transportBytesReceived ?? null,
    setup_transport_at_dtls_packets_sent: dtls?.transportPacketsSent ?? null,
    setup_transport_at_dtls_packets_received: dtls?.transportPacketsReceived ?? null,
    setup_transport_at_dtls_bytes_sent: dtls?.transportBytesSent ?? null,
    setup_transport_at_dtls_bytes_received: dtls?.transportBytesReceived ?? null,

    setup_stun_at_ice_requests_sent: ice?.requestsSent ?? null,
    setup_stun_at_ice_responses_received: ice?.responsesReceived ?? null,
    setup_stun_at_ice_consent_requests_sent: ice?.consentRequestsSent ?? null,
    setup_stun_at_dtls_requests_sent: dtls?.requestsSent ?? null,
    setup_stun_at_dtls_responses_received: dtls?.responsesReceived ?? null,
    setup_stun_at_dtls_consent_requests_sent: dtls?.consentRequestsSent ?? null,

    setup_handshake_avg_bytes_sent: avgPacketBytes(
      ice?.transportBytesSent,
      dtls?.transportBytesSent,
      ice?.transportPacketsSent,
      dtls?.transportPacketsSent,
    ),
    setup_handshake_avg_bytes_received: avgPacketBytes(
      ice?.transportBytesReceived,
      dtls?.transportBytesReceived,
      ice?.transportPacketsReceived,
      dtls?.transportPacketsReceived,
    ),
    setup_ice_to_dtls_ms:
      t?.dtlsConnectedMs != null && t?.iceConnectedMs != null
        ? Math.max(0, t.dtlsConnectedMs - t.iceConnectedMs)
        : null,

    // Read from the rolling snapshot rather than the dtls latch, and NOT only because a
    // settled row always has both. The reason is the unsettled path: when DTLS never
    // completes, `atDtlsConnected` is null, so reading from the latch would emit
    // null-because-no-latch where the snapshot emits null-because-genuinely-absent from
    // the last live poll. Same value on a good row, better provenance on a bad one.
    setup_dtls_cipher: s?.dtlsCipher ?? null,
    setup_dtls_role: s?.dtlsRole ?? null,
    setup_tls_version: s?.tlsVersion ?? null,
    setup_selected_pair_changes: s?.selectedCandidatePairChanges ?? null,
    setup_sdp_local_role: t?.sdpLocalRole ?? null,
    setup_sdp_remote_role: t?.sdpRemoteRole ?? null,
    setup_local_cert_bytes: s?.localCertBytes ?? null,
    setup_remote_cert_bytes: s?.remoteCertBytes ?? null,
    setup_ice_packets_discarded_on_send: s?.packetsDiscardedOnSend ?? null,
    setup_ice_bytes_discarded_on_send: s?.bytesDiscardedOnSend ?? null,
    // Cumulative seconds over the response count. Guarded because zero responses divides
    // to Infinity, which serialises as null and would read as "not observed" rather than
    // "nothing came back" — opposite meanings.
    setup_stun_rtt_mean_ms:
      s?.totalRoundTripTime != null && (s?.responsesReceived ?? 0) > 0
        ? Math.round((s.totalRoundTripTime / (s.responsesReceived as number)) * 1000)
        : null,
    setup_local_network_type: s?.localNetworkType ?? null,
    setup_conn_type: t?.connType ?? null,
    setup_conn_effective_type: t?.connEffectiveType ?? null,
    setup_conn_downlink_mbps: t?.connDownlinkMbps ?? null,
    setup_conn_rtt_ms: t?.connRttMs ?? null,
  };
}

// The one private reach. Twilio exposes no peer connection, no getStats and no
// ICE/DTLS/signalling event on the public Call; its own preflight reads this same path
// (lib/twilio/preflight/preflight.ts:543). close() sets version.pc = null while version
// stays truthy (peerconnection.ts:921), so every hop is optional.
export function peerConnectionOf(call: unknown): PeerConnectionLike | null {
  try {
    const pc = (
      call as {
        _mediaHandler?: { version?: { pc?: PeerConnectionLike | null } };
      }
    )?._mediaHandler?.version?.pc;
    return pc && typeof pc.getStats === 'function' && typeof pc.addEventListener === 'function'
      ? pc
      : null;
  } catch {
    return null;
  }
}

// Distinct known schemes only. URLs and credentials are never read: getConfiguration()
// returns the live Twilio NTS username/credential, and a malformed url without a colon
// would otherwise be emitted whole.
function schemesOf(servers: { urls?: string | string[] }[] | undefined): string | null {
  if (!Array.isArray(servers)) return null;
  const out = new Set<string>();
  for (const server of servers) {
    const urls = server?.urls;
    for (const url of Array.isArray(urls) ? urls : urls ? [urls] : []) {
      const scheme = str(url)?.split(':')[0]?.toLowerCase();
      if (scheme && ICE_SCHEMES.has(scheme)) out.add(scheme);
    }
  }
  return out.size > 0 ? [...out].sort().join(',') : null;
}

// Wires listeners and the stats poll. Returns an idempotent detach.
//
// addEventListener throughout, never property assignment: the SDK owns pc.onicecandidate,
// pc.oniceconnectionstatechange and friends as PROPERTIES (peerconnection.ts:644-712), so
// additive listeners cannot clobber them. Preflight's own trick of wrapping
// _mediaHandler.on* is rejected — that puts this code inside the SDK's call-control path,
// where one throw would break call state on a live call.
export function attachSetupProbe(call: unknown, t: SetupTiming): () => void {
  const pc = peerConnectionOf(call);
  if (!pc) {
    t.probe = 'no_pc';
    return () => {
      t.stopped = true;
    };
  }
  t.probe = 'ok';

  // A fold that throws is counted, never promoted to an attach verdict: `probe` must stay
  // answerable as "did we get a peer connection", or a `setup_probe:ok` filter silently
  // drops otherwise-complete rows.
  const guard =
    (fn: (ev: PcEventLike) => void) =>
    (ev: PcEventLike): void => {
      try {
        fn(ev);
      } catch {
        t.probeErrors += 1;
      }
    };

  try {
    t.attachIceState = str(pc.iceConnectionState);
    t.attachGatheringState = str(pc.iceGatheringState);
    t.attachSignalingState = str(pc.signalingState);
    const config = pc.getConfiguration?.();
    if (config) {
      t.iceServers = Array.isArray(config.iceServers) ? config.iceServers.length : null;
      t.iceServerSchemes = schemesOf(config.iceServers);
      t.iceTransportPolicy = str(config.iceTransportPolicy);
    }
    // Not in lib.dom and Chromium-only, so read structurally. Expect `type` to be null on
    // desktop: Chrome ships it on Android and ChromeOS only, which means the one member
    // that would name wifi-vs-wired-vs-cellular is the one a desktop fleet does not get,
    // leaving the local candidate's `networkType` as the only such signal. `downlink` is
    // capped at 10 Mbps and `rtt` rounded to 25ms as anti-fingerprinting measures, so they
    // can falsify "the link is slow" but can never confirm "the link is fast".
    const conn = (navigator as { connection?: Record<string, unknown> }).connection;
    if (conn) {
      t.connType = str(conn['type']);
      t.connEffectiveType = str(conn['effectiveType']);
      t.connDownlinkMbps = num(conn['downlink']);
      t.connRttMs = num(conn['rtt']);
    }
  } catch {
    t.probeErrors += 1;
  }

  // Four rows of a table, not four hand-written handlers. `pc` is read at fire time
  // because these events carry the new state on the connection, not on the event.
  const stateEvents: [string, 'ice' | 'pc' | 'gathering', (ev: PcEventLike) => string][] = [
    ['iceconnectionstatechange', 'ice', () => pc.iceConnectionState ?? ''],
    // Thin-client stacks leave pc.connectionState undefined and put it on the target —
    // the SDK carries the identical fallback (peerconnection.ts:657, "VDI environment").
    // Without it this mark is null on an identifiable slice of the fleet, not at random.
    [
      'connectionstatechange',
      'pc',
      (ev) =>
        pc.connectionState || ev?.target?.connectionState || ev?.target?.connectionState_ || '',
    ],
    ['icegatheringstatechange', 'gathering', () => pc.iceGatheringState ?? ''],
  ];

  const listeners: [string, (ev: PcEventLike) => void][] = stateEvents.map(([name, kind, read]) => [
    name,
    guard((ev) => observeState(t, kind, read(ev), performance.now())),
  ]);
  listeners.push([
    'icecandidate',
    guard((ev) => {
      // A null candidate is the end-of-gathering sentinel; an empty candidate string is
      // the same signal from stacks that send an object. Neither is a route.
      const c = ev?.candidate;
      if (!c || c.candidate === '') return;
      observeCandidate(t, str(c.type), performance.now());
    }),
  ]);

  for (const [name, fn] of listeners) {
    try {
      pc.addEventListener(name, fn);
    } catch {
      t.probeErrors += 1;
    }
  }

  let timer: ReturnType<typeof setTimeout> | null = null;
  // One expression, used by both the scheduler and the tick, so the stop condition cannot
  // drift between them.
  const done = (): boolean =>
    t.stopped || setupSettled(t) || performance.now() - t.startedAt > MAX_POLL_MS;

  const tick = (): void => {
    timer = null;
    // `t` is captured directly and never read through a ref, so a poll belonging to a
    // finished call writes into that call's own stopped object and is discarded — one
    // call's numbers can never be attributed to the next.
    if (done()) return;
    // Polled rather than read once at attach, because the remote description may not be
    // set at the moment the probe attaches. Short-circuited once both roles are known, so
    // a latch-once value is not re-parsed across every poll of the call.
    if (t.sdpLocalRole == null || t.sdpRemoteRole == null) {
      try {
        observeSdp(t, pc.currentLocalDescription?.sdp, pc.currentRemoteDescription?.sdp);
      } catch {
        t.probeErrors += 1;
      }
    }
    let pending: Promise<StatsReportLike> | null = null;
    try {
      pending = pc.getStats();
    } catch {
      // Synchronous throw (legacy getStats shapes) — keep polling.
      t.probeErrors += 1;
    }
    if (!pending) {
      schedule();
      return;
    }
    pending
      .then((report) => {
        if (!t.stopped) observeStats(t, parseStats(report), performance.now());
      })
      .catch(() => {
        // The pc may be mid-teardown; a rejected poll is expected, not a failure.
      })
      .finally(schedule);
  };
  function schedule(): void {
    if (done() || timer != null) return;
    timer = setTimeout(tick, POLL_MS);
  }
  // Poll ONCE immediately. Deferring the first read by POLL_MS would floor every polled
  // mark at 250ms while the event-driven marks stayed exact — and subtracting the two
  // families is the whole point. On a ~700ms fast-path call that floor is a third of the
  // window, and it can render DTLS as completing after the connection did.
  tick();

  return () => {
    t.stopped = true;
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
    for (const [name, fn] of listeners) {
      try {
        pc.removeEventListener(name, fn);
      } catch {
        // Detaching a torn-down pc is not a failure worth recording.
      }
    }
  };
}
